import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import axios from 'axios';

// Get backend URL
const getBackendURL = () => {
  return process.env.REACT_APP_BACKEND_URL || 'https://6c1f0f70-88de-468f-b801-d4c2bfd41f7b.preview.emergentagent.com';
};

const BACKEND_URL = getBackendURL();
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

// Global credits state
let globalCredits = 0;
let globalSetters = new Set();

// Custom hook for credits management
export const useCredits = () => {
  const [credits, setCredits] = useState(globalCredits);
  const { publicKey, connected } = useWallet();

  // Register this component's setter
  useEffect(() => {
    globalSetters.add(setCredits);
    return () => {
      globalSetters.delete(setCredits);
    };
  }, []);

  // Function to update all components' credits
  const updateAllCredits = useCallback((newCredits) => {
    globalCredits = newCredits;
    globalSetters.forEach(setter => setter(newCredits));
  }, []);

  // Fetch credits from backend
  const fetchCredits = useCallback(async () => {
    if (!publicKey || !connected) {
      updateAllCredits(0);
      return;
    }

    try {
      const response = await axios.get(`${API}/user-credits/${publicKey.toString()}`);
      const newCredits = response.data.credits || 0;
      updateAllCredits(newCredits);
      console.log('🔄 Credits fetched:', newCredits);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      // Don't reset to 0 on error, keep current value
    }
  }, [publicKey, connected, updateAllCredits]);

  // Auto-fetch credits when wallet connects/changes
  useEffect(() => {
    if (connected && publicKey) {
      fetchCredits();
    } else {
      updateAllCredits(0);
    }
  }, [connected, publicKey, fetchCredits, updateAllCredits]);

  // Function to trigger a credits refresh (for after purchases)
  const refreshCredits = useCallback(async () => {
    console.log('🔄 Refreshing credits after purchase...');
    await fetchCredits();
  }, [fetchCredits]);

  return {
    credits,
    fetchCredits,
    refreshCredits,
    isLoading: !connected && credits === 0
  };
};
